import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ContentLayout } from '../Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { Paginator } from '@/components/Paginator/Paginator';
import { NoResults } from '@/components/NoResults/NoResults';
import NoTransactionsIcon from '../../images/NoTransactionsIcon.svg?react';
import { CreateTransactionForm } from './ui/CreateTransactionForm/CreateTransactionForm';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { TransactionsList } from './ui/TransactionsList';
import { ModalWindow } from '@/components/ModalWindow/ModalWindow';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTransaction, loadTransactions } from './api/api';
import { formatPayloadDate } from '@/helpers/dateHelpers/dateHelpers';
import PlusIcon from '@/images/PlusIcon.svg?react';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { Button } from '@/components/Button/Button';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { Box } from '@/components/Box/Box';
import {
  SearchFilterRadioOptionType,
  TransactionsSearchFilters,
} from './ui/TransactionsSearchFilters/TransactionsSearchFilters';
import { useFilterSearch } from './hooks/useFilterSearch';
import { NoSearchResults } from '../shared/components/NoSearchResults';
import { SEARCH_FILTER_RADIO_OPTION_TYPE, SearchFiltersType } from './ui/TransactionsSearchFilters/const/const';
import { useSubscriptionStatus } from '../shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '../shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { getSearchListData } from '@/helpers/helpers';
import { DashboardTrialModeModal } from '@/bundle/_OrgAdmin/ui/DashboardTrialModeModal/DashboardTrialModeModal';
import { ONBOARDING_GUIDE_STEP_ID } from '@/context/OnboardingGuideContext/const';
import { useOnboardingGuide } from '@/context/OnboardingGuideContext/onboardingGuideContext';

export type ValuesType = {
  expected_close_date: string;
  name: string;
};

export const TransactionsPage = () => {
  const { state } = useLocation();
  const queryClient = useQueryClient();
  const { isViewModeSubscription, isInactiveSubscription } = useSubscriptionStatus();
  const { search, setSearch, submitSearch, clearSearch, searchFilters, changeSearchFilters, searchParams } =
    useFilterSearch();
  const { initGuide, showStep, isInitializedFirstStep, closeGuideStep } = useOnboardingGuide();

  const [isOpen, setIsOpen] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState('');
  const [isOpenTrialModal, setIsOpenTrialModal] = useState(false);

  useEffect(() => {
    if (state?.showTrialModeModal) {
      setIsOpenTrialModal(true);
    }
  }, [state]);

  useEffect(() => {
    if (isInitializedFirstStep) {
      showStep(ONBOARDING_GUIDE_STEP_ID.CREATE_TRANSACTION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializedFirstStep]);

  const closeTrialModeModal = () => {
    setIsOpenTrialModal(false);
    initGuide();

    if (state?.showTrialModeModal) {
      window.history.replaceState(null, '');
    }
  };

  const {
    mutate,
    data: createdTransaction,
    isPending: isPendingCreateTransaction,
    reset,
  } = useMutation({
    mutationKey: ['create_transaction'],
    mutationFn: ({ name, expected_close_date }: ValuesType) => {
      const formattedDate = formatPayloadDate(expected_close_date);
      const payload = { name, expected_close_date: formattedDate };

      return createTransaction(payload);
    },
    onSuccess(data) {
      if (data.error) return;

      closeGuideStep();
      setIsOpen(false);

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateTransactionSuccess);
      queryClient.removeQueries({ queryKey: ['load_transactions', searchParams] });
    },
  });

  const { data: transactions, isPending: isPendingTransactions } = useQuery({
    queryKey: ['load_transactions', searchParams],
    queryFn: () => loadTransactions(searchParams),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (searchFilters.byName) {
      return setSearchPlaceholder('Search by transaction name');
    }

    if (searchFilters?.searchEmailTypes) {
      return setSearchPlaceholder('Search by email address');
    }
  }, [searchFilters]);

  const onClose = () => {
    setIsOpen(false);
    reset();
  };

  const openCreateTransactionForm = () => {
    setIsOpen(true);

    amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateTransactionRedirect);
  };

  const handleSubmitSearch = () => {
    if (!!search.length && searchParams.name !== search) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.TransactionsSearchSubmit);
    }

    submitSearch();
  };

  const handleChangeSearchFilters = (filters: SearchFiltersType, type: SearchFilterRadioOptionType) => {
    changeSearchFilters(filters);

    if (type === SEARCH_FILTER_RADIO_OPTION_TYPE.NAME) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.TransactionsSearchSelectNameFilter);
    }

    if (type === SEARCH_FILTER_RADIO_OPTION_TYPE.EMAIL) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.TransactionsSearchSelectEmailFilter);
    }
  };

  const { list, hasData, hasPagination, totalCount, noResults, noSearchResults } = getSearchListData(
    transactions?.body,
    searchParams,
    isPendingTransactions
  );
  const hasFiltersSearch = (hasData && !isPendingTransactions) || !!searchParams.name;

  return (
    <ContentLayout
      title='Transactions'
      breadcrumbs='Transactions'
      headerAction={
        <Button
          id={ONBOARDING_GUIDE_STEP_ID.CREATE_TRANSACTION}
          disabled={isViewModeSubscription}
          width={220}
          mobileStretch
          icon={<PlusIcon className={isViewModeSubscription ? 'disabled-svg-icon' : ''} />}
          iconPosition='left'
          onClick={openCreateTransactionForm}
        >
          Create Transaction
        </Button>
      }
      offsetSize='small'
      banner={isInactiveSubscription && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        {hasFiltersSearch && (
          <Box width='100%' display='flex'>
            <Box width='344px' mobile={{ width: '100%' }}>
              <SearchInput
                placeholder={searchPlaceholder}
                value={search}
                filters={
                  <TransactionsSearchFilters
                    searchFilters={searchFilters}
                    onChangeSearchFilters={handleChangeSearchFilters}
                  />
                }
                onChange={(value) => setSearch(value)}
                onClear={clearSearch}
                onSubmit={handleSubmitSearch}
              />
            </Box>
          </Box>
        )}
        {hasData && <TransactionsList list={list} />}
        {noResults && <NoResults icon={<NoTransactionsIcon />} message='Please create your first transaction.' />}
        {noSearchResults && <NoSearchResults>No transactions found</NoSearchResults>}
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}
      <ModalWindow isOpen={isOpen} headerText='Create Transaction' isStartPositionHeader width='504px'>
        <CreateTransactionForm
          onCreate={mutate}
          apiError={createdTransaction?.error}
          onClose={onClose}
          isLoading={isPendingCreateTransaction}
        />
      </ModalWindow>
      <DashboardTrialModeModal isOpen={isOpenTrialModal} onClose={closeTrialModeModal} />
    </ContentLayout>
  );
};
